import React, { FC, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { SpotBiddingRule } from 'apps/marketplace/apiTypes/spotBiddingConfigs'
import { IShipper } from 'apps/spot-quote-control-panel/types/shipperStrikePrices'
import { useModal } from 'utils/hooks'
import { sortRulesByRegion } from '../utils/shipperStrikePrice'
import { StrikePriceDeleteRuleModal } from './StrikePriceDeleteRuleModal'
import { StrikePriceRuleCollection } from './StrikePriceRuleCollection'
import { StrikePriceRuleModal } from './StrikePriceRuleModal'

const useStyles = makeStyles(theme => ({
  container: {
    width: '350px',
    flexBasis: '350px',
    flexGrow: 0,
    flexShrink: 0,
    backgroundColor: '#fafbfb',
    borderRight: `1px solid ${brandColors.coolGray4}`,
    padding: theme.spacing(2.5),
  },
}))

interface IStrikePriceRulesSidebarProps {
  currentShipper?: IShipper
  globalRules: SpotBiddingRule[]
  globalRulesLoading: boolean
  globalRulesError?: string
  shipperRules: SpotBiddingRule[]
  shipperRulesLoading: boolean
  shipperRulesError?: string
  reloadRules: () => void
}

export const StrikePriceRulesSidebar: FC<IStrikePriceRulesSidebarProps> = ({
  currentShipper,
  globalRules,
  globalRulesError,
  globalRulesLoading,
  reloadRules,
  shipperRules,
  shipperRulesError,
  shipperRulesLoading,
}) => {
  const classes = useStyles()
  const [currentRule, setCurrentRule] = useState<SpotBiddingRule | undefined>(undefined)
  const [modalShipperId, setModalShipperId] = useState<number | undefined>(undefined)
  const { closeModal, isOpen, openModal } = useModal()
  const [ruleModal, deleteRuleModal] = ['ruleModal', 'deleteRuleModal']
  const openRuleModal = openModal(ruleModal)
  const closeRuleModal = () => {
    setCurrentRule(undefined)
    closeModal()
  }
  const addOrEditRule = (rule?: SpotBiddingRule, isGlobal?: boolean) => {
    setModalShipperId(isGlobal ? undefined : currentShipper?.id)
    setCurrentRule(rule)
    openRuleModal()
  }
  const addGlobalRule = () => addOrEditRule(undefined, true)
  const addShipperRule = () => addOrEditRule()
  const editGlobalRule = (rule?: SpotBiddingRule) => addOrEditRule(rule, true)
  const editShipperRule = (rule?: SpotBiddingRule) => addOrEditRule(rule)
  const ruleRequestSuccessHandler = () => {
    closeModal()
    reloadRules()
  }

  const sortedGlobalRules = sortRulesByRegion(globalRules)
  const sortedShipperRules = sortRulesByRegion(shipperRules)

  return (
    <>
      <Box className={classes.container}>
        <Box mb={1}>
          <Typography variant='overline'>Rules</Typography>
        </Box>
        <Box mb={1} display='flex' alignItems='center' justifyContent='space-between'>
          <Box>
            <Typography variant='h3'>Global Rules</Typography>
          </Box>
          <Box>
            <Button color='secondary' onClick={addGlobalRule}>
              + Add Rule
            </Button>
          </Box>
        </Box>
        {globalRulesError && (
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={globalRulesError} />
        )}
        {globalRulesLoading && <Spinner position='relative' />}
        {globalRules.length > 0 &&
          Object.keys(sortedGlobalRules)
            .sort((a, b) => {
              if (a === 'Lane') return -1
              if (b === 'Lane') return 1
              return a.localeCompare(b)
            })
            .map(region => (
              <StrikePriceRuleCollection
                key={region}
                label={region}
                rules={sortedGlobalRules[region]}
                ruleEditHandler={editGlobalRule}
                ruleDeleteSuccessHandler={ruleRequestSuccessHandler}
              />
            ))}
        {currentShipper && (
          <>
            <Box mb={1} display='flex' alignItems='center' justifyContent='space-between'>
              <Box>
                <Typography variant='h3'>Shipper Rules</Typography>
              </Box>
              <Box>
                <Button color='secondary' onClick={addShipperRule}>
                  + Add Rule
                </Button>
              </Box>
            </Box>
            {shipperRulesError && (
              <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={shipperRulesError} />
            )}
            {shipperRulesLoading && <Spinner position='relative' />}
            {shipperRules.length > 0 &&
              Object.keys(sortedShipperRules)
                .sort((a, b) => {
                  if (a === 'Step Pricing') return -1
                  if (b === 'Step Pricing') return 1
                  if (a === 'Lane') return -1
                  if (b === 'Lane') return 1
                  return a.localeCompare(b)
                })
                .map(region => (
                  <StrikePriceRuleCollection
                    key={region}
                    label={region}
                    rules={sortedShipperRules[region]}
                    ruleEditHandler={editShipperRule}
                    ruleDeleteSuccessHandler={ruleRequestSuccessHandler}
                  />
                ))}
          </>
        )}
      </Box>
      {isOpen(ruleModal) && (
        <StrikePriceRuleModal
          shipperId={modalShipperId}
          spotBiddingRule={currentRule}
          onClose={closeRuleModal}
          onRequestSuccess={ruleRequestSuccessHandler}
        />
      )}
      {isOpen(deleteRuleModal) && (
        <StrikePriceDeleteRuleModal
          ruleId={currentRule?.id}
          onClose={closeModal}
          onRequestSuccess={ruleRequestSuccessHandler}
        />
      )}
    </>
  )
}
